import { getSessionId } from '@linktr.ee/client-storage';

const SESSION_ID_STORAGE_KEY = 'sessionId';

let generatedClientSessionId: string | undefined;

const getStoredClientSessionId = (): string => {
  try {
    return getSessionId();
  } catch {
    return '';
  }
};

const persistClientSessionId = (sessionId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  for (const storageKey of ['localStorage', 'sessionStorage'] as const) {
    try {
      window[storageKey].setItem(SESSION_ID_STORAGE_KEY, sessionId);
    } catch {
      // Some embedded WebViews block storage. The in-memory fallback still
      // keeps Descope action payloads joinable for this page lifetime.
    }
  }
};

const generateFallbackClientSessionId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes =
    typeof globalThis.crypto?.getRandomValues === 'function'
      ? globalThis.crypto.getRandomValues(new Uint8Array(16))
      : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));

  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0'));

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};

export const getOrCreateClientSessionId = (): string => {
  const storedSessionId = getStoredClientSessionId();
  if (storedSessionId) {
    return storedSessionId;
  }

  if (!generatedClientSessionId) {
    generatedClientSessionId = generateFallbackClientSessionId();
    persistClientSessionId(generatedClientSessionId);
  }

  return generatedClientSessionId;
};
